import React from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/default-input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useTRPC } from "@/trpc";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as Icons from "lucide-react";
import { useFormContext } from "react-hook-form";
import { FilterState, normalizeFilterInput } from "../(context)/filter";
import { TPlayerSearch, TShortlistAll } from "@/types/trpc.types";
import { toast } from "sonner";
import { TRPCClientError } from "@trpc/client";
import { v4 as uuidv4 } from 'uuid';
import { ROUTES_CONFIG } from "@/config/routes.config";
import { Link, useNavigate } from "react-router-dom";
import { convertNumberToMarketValue } from "@/lib/utils";
import { hasProperties } from "@/lib/ts-helpers";
import { hasRight } from "@/config/rights.config";
import { useAuth } from "@/hooks/use-auth";
import { SelectListDialog } from "@/components/features/players/select-longlist-shortlist";

type Player = TPlayerSearch['players'][number]

type Props = {
  onRefetchReady?: (fn: () => void) => void;
};

const OverviewTable = ({ onRefetchReady }: Props) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { trpc } = useTRPC();
  const { watch, getValues, setValue } = useFormContext<FilterState>();
  const filterState = watch();

  const query = useQuery({
    queryKey: ["players", filterState],
    queryFn: async () => {
      const input = normalizeFilterInput(getValues());
      const players = await trpc.players.search.query(input);
      return players;
    },
    enabled: false,
  });

  const { data:shortlist } = useQuery({
    queryKey: ['shortlist'],
    queryFn: async () => {
      const shortlist = await trpc.shortlist.getAll.query();
      return shortlist;
    }
  })

  const { data:longlist } = useQuery({
    queryKey: ['longlist'],
    queryFn: async () => {
      const longlist = await trpc.longlist.getAll.query();
      return longlist;
    }
  });

  React.useEffect(() => {
    onRefetchReady?.(() => query.refetch());
  }, [query.refetch]);

  React.useEffect(() => {
    query.refetch();
  }, [filterState.page]);

  return (
    <div className="h-full overflow-auto">
      <div className="flex flex-col justify-between gap-4 h-full">
        <div className="bg-foreground/10 rounded-md border pb-2 pr-2 pl-2 overflow-auto h-full">
          <Table className='overflowclip w-full relative' divClassname='overflow-y-scroll max-h-[100%]'>
            <TableHeader className="sticky top-0 w-full border-b bg-sidebar/50 backdrop-blur-md z-10">
              <TableRow>
                {hasRight(user?.role?.name, "SHORTLIST.WRITE") && <TableHead className="w-10"></TableHead>}
                <TableHead>#</TableHead>
                <TableHead>Naam</TableHead>
                <TableHead>Pos.</TableHead>
                <TableHead>Geboortedatum</TableHead>
                <TableHead>Nat.</TableHead>
                <TableHead>Club</TableHead>
                <TableHead>Lengte</TableHead>
                <TableHead>Marktwaarde</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {query?.data?.players?.map((player) => {
                const club = typeof player.club === "object" ? player.club : undefined;
                const isInShortlist = shortlist?.shortlist?.find(s => s.mongo_player_id === player.id);
                const isInLonglist = longlist?.longlist?.find(l => l.mongo_player_id === player.id);

                return (
                  <TableRow
                    key={player.id}
                    className="hover:bg-foreground/10 group/row"
                  >
                  {hasRight(user?.role?.name, "SHORTLIST.WRITE") && 
                    <TableCell>
                      {(isInShortlist && isInLonglist) ? (
                        <div className="relative">
                            <Icons.CheckIcon className="w-4 h-4 text-bright absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-100"/>
                        </div>    
                      ) : (
                        <SelectListDialog
                          Player={player as Player}
                          isInShortlist={!!isInShortlist}
                          isInLonglist={!!isInLonglist}
                          onSuccess={() => {
                            query.refetch();
                          }}
                          trigger={
                            <Button 
                              variant="bright" 
                              shape="pill" 
                              size="icon" 
                              className="group-hover/row:opacity-100 opacity-0"
                            >
                              <Icons.UserPlus className="w-4 h-4"/>
                            </Button>
                          }
                        />
                      )}                      
                    </TableCell>
                    }
                    <TableCell>{player?.shirtNumber || "-"}</TableCell>
                    <TableCell 
                      className="group/name cursor-pointer"
                      onClick={()=>{
                        if(player?.id === undefined) return;
                        navigate(ROUTES_CONFIG.APP.PLAYER_DETAIL.$buildPath({ params: { player_id: player?.id } }))
                      }}
                    >
                      <div className="flex items-center gap-2">
                        <div className="size-8 aspect-square rounded-full bg-foreground/10">
                          <Avatar>
                            <AvatarImage
                              src={player?.imageUrl ?? undefined}
                              alt={player?.name ?? undefined}
                            />
                            <AvatarFallback>
                              {player?.name?.charAt(0)}
                            </AvatarFallback>
                          </Avatar>
                        </div>
                        <p className="group-hover/name:underline font-medium">{player?.name || "-"}</p>
                      </div>
                    </TableCell>
                    <TableCell>{player?.positionMain|| "-"}</TableCell>
                    <TableCell>
                      {player?.dateOfBirth} {player?.age ? `(${player?.age})` : "-"}
                    </TableCell>
                    <TableCell>{player?.citizenship?.join(", ")}</TableCell>
                    <TableCell>

                        <div
                          onClick={() => {
                            if (!club?.id) return;
                            navigate(
                              ROUTES_CONFIG.APP.CLUB_DETAIL.$buildPath({
                                params: { club_id: club.id.toString() },
                              })
                            );
                          }}
                          className="cursor-pointer hover:underline flex items-center gap-2"
                        >
                          {club?.imageUrl && (
                            <div className="size-8 aspect-square rounded-full overflow-hidden flex items-center justify-center">
                              <img
                                src={club.imageUrl}
                                className="w-full h-full object-contain"
                              />
                            </div>
                          )}
                          <p>{club?.name}</p>
                        </div>
                      
                    </TableCell>
                    <TableCell>{player?.height || "-"}</TableCell>
                    <TableCell>{convertNumberToMarketValue(player?.marketValue) || "-"}</TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </div>

        <div className="flex items-center justify-between gap-4">
        <p className="text-sm font-medium text-foreground/50">
          {query?.data?.total} rijen gevonden
        </p>

        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            onClick={() => setValue("page", 1)}
            disabled={parseInt(filterState?.page?.toString() || "0") <= 1}
          >
            <Icons.ChevronsLeft className="w-4 h-4" />
          </Button>
          <Button
            variant="outline"
            onClick={() =>
              setValue("page", Math.max(1, Number(filterState.page) - 1))
            }
            disabled={parseInt(filterState?.page?.toString() || "0") <= 1}
          >
            <Icons.ChevronLeft className="w-4 h-4" />
          </Button>
          <Input 
            value={filterState.page}
            onChange={(e) => {
              setValue('page', e.currentTarget.valueAsNumber);
            }}
            onBlur={() => {
              query.refetch();
            }}
            type="number"
            min={1}
            max={query?.data?.pages || 1}
          />
          <Button
            variant="outline"
            onClick={() =>
              setValue(
                "page",
                Math.min(
                  Number(query?.data?.pages || 1),
                  Number(filterState.page) + 1
                )
              )
            }
            disabled={Number(filterState.page) >= Number(query?.data?.pages)}
          >
            <Icons.ChevronRight className="w-4 h-4" />
          </Button>
          <Button
            variant="outline"
            onClick={() => setValue("page", query?.data?.pages || 1)}
            disabled={Number(filterState.page) >= Number(query?.data?.pages)}
          >
            <Icons.ChevronsRight className="w-4 h-4" />
          </Button>
        </div>
        </div>
      </div>
    </div>
  );
};

export default OverviewTable;
